import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../common/Button';
import { Avatar, AvatarImage, AvatarFallback } from '../common/Avatar';
import { useThemeContext } from '@/context/ThemeContext';
import {
    Home,
    Calendar,
    Users,
    PawPrint,
    Building2,
    Settings,
    LogOut,
    Menu,
    X,
    Bell,
    MessageCircle,
    BarChart3,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    User,
    Settings2,
    ChevronsLeft,
    ChevronsRight,
    FileText,
    Send,
    LayoutTemplate,
    Calculator,
    Coins,
    LayoutDashboard,
    List,
    Moon,
    Sun,
} from 'lucide-react';

interface SidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    onLogout?: () => void;
    isCollapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
}

interface SubMenuItem {
    label: string;
    href: string;
    tab: string;
    icon: React.ElementType;
}

interface NavItem {
    label: string;
    href: string;
    icon: React.ElementType;
    subItems?: SubMenuItem[];
}

const navItems: NavItem[] = [
    { label: '홈', href: '/', icon: Home },
    { label: '고객 관리', href: '/users', icon: Users },
    { label: '반려동물', href: '/pets', icon: PawPrint },
    { label: '일정 관리', href: '/schedules', icon: Calendar },
    {
        label: '하우스 관리',
        href: '/houses',
        icon: Building2,
    },
    {
        label: '알림장 관리',
        href: '/report',
        icon: FileText,
        subItems: [
            { label: '템플릿 관리', href: '/report', tab: 'templates', icon: LayoutTemplate },
            { label: '작성 및 발송', href: '/report', tab: 'write', icon: Send },
        ],
    },
    { label: '정산 관리', href: '/billing', icon: Coins },
    { label: '이용분석', href: '/analytics', icon: BarChart3 },
    // { label: '채팅', href: '/chat', icon: MessageCircle},
    {
        label: '설정',
        href: '/settings',
        icon: Settings,
        subItems: [
            { label: '매니저 정보', href: '/settings', tab: 'profile', icon: User },
            { label: '지점 정보', href: '/settings', tab: 'branch', icon: Building2 },
            { label: '운영 관리', href: '/settings', tab: 'operations', icon: Settings2 },
            { label: '동의서 관리', href: '/settings', tab: 'consent', icon: FileText },
        ],
    },
];

/** 접힌 사이드바에서 hover 시 하위 메뉴를 flyout(Portal)으로 표시 */
function CollapsedSubMenu({ item, isActive, currentTab }: { item: NavItem; isActive: boolean; currentTab: string }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const location = useLocation();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const Icon = item.icon;

    const updatePos = useCallback(() => {
        if (!btnRef.current) return;
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.top, left: r.right + 4 });
    }, []);

    const handleEnter = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        updatePos();
        setOpen(true);
    };
    const handleLeave = () => {
        timerRef.current = setTimeout(() => setOpen(false), 150);
    };

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    return (
        <li
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
        >
            <button
                ref={btnRef}
                type="button"
                className={cn(
                    'relative flex w-full items-center justify-center rounded-lg p-2 transition-colors',
                    isActive
                        ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                        : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                )}
            >
                <Icon className="h-5 w-5" />
                <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 h-2.5 w-2.5 opacity-50" />
            </button>

            {open && createPortal(
                <div
                    className="fixed z-[9999] min-w-[160px] rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1"
                    style={{ top: pos.top, left: pos.left }}
                    onMouseEnter={handleEnter}
                    onMouseLeave={handleLeave}
                >
                    <div className="px-3 py-1.5 text-xs font-semibold text-neutral-400 dark:text-neutral-500">
                        {item.label}
                    </div>
                    {item.subItems?.map(subItem => {
                        const SubIcon = subItem.icon;
                        const isSubActive = subItem.tab
                            ? location.pathname === subItem.href && currentTab === subItem.tab
                            : location.pathname === subItem.href;
                        return (
                            <Link
                                key={`${subItem.href}-${subItem.tab}`}
                                to={subItem.tab ? `${subItem.href}?tab=${subItem.tab}` : subItem.href}
                                onClick={() => setOpen(false)}
                                className={cn(
                                    'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                                    isSubActive
                                        ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                                        : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                                )}
                            >
                                <SubIcon className="h-4 w-4" />
                                {subItem.label}
                            </Link>
                        );
                    })}
                </div>,
                document.body
            )}
        </li>
    );
}

export function Sidebar({ isOpen, onToggle, onLogout, isCollapsed = false, onCollapsedChange }: SidebarProps) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
    const filteredNavItems = navItems;

    // 현재 경로에 해당하는 상위 메뉴를 자동으로 열기
    useEffect(() => {
        const currentPath = location.pathname;
        navItems.forEach(item => {
            if (item.subItems && item.subItems.some(sub => sub.href === currentPath)) {
                setExpandedMenus(prev => {
                    if (!prev.has(item.label)) {
                        const next = new Set(prev);
                        next.add(item.label);
                        return next;
                    }
                    return prev;
                });
            }
        });
    }, [location.pathname]);

    const toggleSubmenu = (label: string) => {
        setExpandedMenus(prev => {
            const next = new Set(prev);
            if (next.has(label)) {
                next.delete(label);
            } else {
                next.add(label);
            }
            return next;
        });
    };

    const currentTab = searchParams.get('tab') || 'profile';

    const handleCollapseToggle = () => {
        onCollapsedChange?.(!isCollapsed);
    };

    // 접힌 상태에서 아이템 렌더링
    const renderNavItem = (item: NavItem, isActive: boolean) => {
        const Icon = item.icon;
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = expandedMenus.has(item.label);

        if (isCollapsed) {
            // 접힌 상태: 하위 메뉴가 있으면 hover flyout 표시
            if (hasSubItems) {
                return (
                    <CollapsedSubMenu key={item.href} item={item} isActive={isActive} currentTab={currentTab} />
                );
            }

            return (
                <li key={item.href}>
                    <Link
                        to={item.href}
                        title={item.label}
                        className={cn(
                            'flex items-center justify-center rounded-lg p-2 transition-colors',
                            isActive
                                ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                                : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        )}
                    >
                        <Icon className="h-5 w-5" />
                    </Link>
                </li>
            );
        }

        // 펼쳐진 상태: 기존 로직
        if (hasSubItems) {
            return (
                <li key={item.href}>
                    <button
                        type="button"
                        onClick={() => toggleSubmenu(item.label)}
                        className={cn(
                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive
                                ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                                : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <Icon className="h-5 w-5" />
                            {item.label}
                        </div>
                        {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </button>
                    {/* Submenu */}
                    <div
                        className={cn(
                            'overflow-hidden transition-all duration-200',
                            isExpanded ? 'max-h-48' : 'max-h-0'
                        )}
                    >
                        <ul className="mt-1 space-y-1 pl-4">
                            {item.subItems?.map(subItem => {
                                const SubIcon = subItem.icon;
                                const isSubActive = subItem.tab
                                    ? location.pathname === subItem.href && currentTab === subItem.tab
                                    : location.pathname === subItem.href;
                                return (
                                    <li key={`${subItem.href}-${subItem.tab}`}>
                                        <Link
                                            to={subItem.tab ? `${subItem.href}?tab=${subItem.tab}` : subItem.href}
                                            className={cn(
                                                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                                isSubActive
                                                    ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                                                    : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200'
                                            )}
                                        >
                                            <SubIcon className="h-4 w-4" />
                                            {subItem.label}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </li>
            );
        }

        return (
            <li key={item.href}>
                <Link
                    to={item.href}
                    className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                            ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                            : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    )}
                >
                    <Icon className="h-5 w-5" />
                    {item.label}
                </Link>
            </li>
        );
    };

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                    onClick={onToggle}
                />
            )}

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-700 transition-all duration-300 lg:translate-x-0',
                    isOpen ? 'translate-x-0' : '-translate-x-full',
                    isCollapsed ? 'w-16' : 'w-64'
                )}
            >
                {/* Logo */}
                <div className={cn(
                    "flex h-16 items-center border-b border-neutral-200 dark:border-neutral-700",
                    isCollapsed ? "justify-center px-2" : "justify-between px-4"
                )}>
                    <Link to="/" className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500 text-white font-bold shrink-0">
                            T
                        </div>
                        {!isCollapsed && (
                            <span className="text-lg font-semibold text-neutral-700 dark:text-neutral-200">Tailody</span>
                        )}
                    </Link>
                    <div className="flex items-center gap-1">
                        {/* Desktop Collapse Toggle */}
                        <button
                            type="button"
                            title={isCollapsed ? "메뉴 펼치기" : "메뉴 접기"}
                            className="hidden lg:flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                            onClick={handleCollapseToggle}
                        >
                            {isCollapsed ? (
                                <ChevronsRight className="h-4 w-4" />
                            ) : (
                                <ChevronsLeft className="h-4 w-4" />
                            )}
                        </button>
                        {/* Mobile Close Button */}
                        {!isCollapsed && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                onClick={onToggle}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <nav className={cn("flex-1 overflow-y-auto", isCollapsed ? "p-2" : "p-4")}>
                    <ul className="space-y-1">
                        {filteredNavItems.map(item => {
                            const isActive = location.pathname === item.href;
                            return renderNavItem(item, isActive);
                        })}
                    </ul>
                </nav>

                {/* Footer - Logout */}
                <div className={cn("border-t border-neutral-200 dark:border-neutral-700", isCollapsed ? "p-2" : "p-4")}>
                    {isCollapsed ? (
                        <Button
                            variant="ghost"
                            size="icon"
                            title="로그아웃"
                            className="w-full text-neutral-600 hover:text-danger-600 hover:bg-danger-50"
                            onClick={onLogout}
                        >
                            <LogOut className="h-5 w-5" />
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3 text-neutral-600 hover:text-danger-600 hover:bg-danger-50"
                            onClick={onLogout}
                        >
                            <LogOut className="h-5 w-5" />
                            로그아웃
                        </Button>
                    )}
                </div>
            </aside>
        </>
    );
}

interface Group {
    id: number;
    name?: string;
}

interface HeaderProps {
    onMenuClick: () => void;
    userName?: string;
    userImage?: string;
    groups?: Group[];
    selectedGroupId?: number;
    onGroupChange?: (groupId: number) => void;
}

export function Header({ onMenuClick, userName, userImage, groups, selectedGroupId, onGroupChange }: HeaderProps) {
    const { isDark, toggleTheme } = useThemeContext();

    return (
        <header className="fixed top-0 right-0 left-0 lg:left-[var(--sidebar-width)] z-30 flex h-16 items-center justify-between border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 lg:px-6 transition-all duration-300">
            <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={onMenuClick}
            >
                <Menu className="h-5 w-5" />
            </Button>

            {/* Group Selector */}
            {groups && groups.length > 0 && (
                <div className="flex items-center gap-2 ml-4 lg:ml-0">
                    <Building2 className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                    <select
                        value={selectedGroupId || ''}
                        onChange={(e) => onGroupChange?.(Number(e.target.value))}
                        className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 min-w-[150px]"
                    >
                        {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                                {group.name || `그룹 ${group.id}`}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="flex-1" />

            <div className="flex items-center gap-4">
                {/* Dark mode toggle */}
                <button
                    onClick={toggleTheme}
                    className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                        'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                        'dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200'
                    )}
                    title={isDark ? '라이트 모드' : '다크 모드'}
                >
                    {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>

                <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                        <AvatarImage src={userImage} alt={userName} />
                        <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                            {userName?.charAt(0) || 'U'}
                        </AvatarFallback>
                    </Avatar>
                    <span className="hidden text-sm font-medium text-neutral-700 dark:text-neutral-200 sm:block">
                        {userName || '사용자'}
                    </span>
                </div>
            </div>
        </header>
    );
}
